import React, { Fragment, useEffect, useRef, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { api } from '../../api';
import { useStyles } from '../../hooks/useStyles';
import { useAuth } from '../../units/auth/AuthProvider';
import carQueue, { CarQueue } from '../../api/car-queue';
import moment from 'moment';
import { washMicroSeconds, washMinutes } from '../../data/WashTime';
import { green, grey, orange, red } from '@mui/material/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompress, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { useScreenSize } from '../../hooks/useScreenSize';
import { Store } from '../../api/store';
import mqtt, { MqttClient } from 'mqtt';

type DataRow = CarQueue & {
    selected: boolean;
    index: number;
    prevCarQueue: DataRow | undefined;
    nextCarQueue: DataRow | undefined;
    prevSpaces: number;
    nextSpaces: number;
    estimateUnixTime: number;
    warnings: string[];
};

export const useCarQueueDashboardPage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const [myCarIds, setMyCarIds] = useState<string[]>([]);
    const [carQueues, setCarQueues] = useState<CarQueue[]>([]);
    const [rows, setRows] = useState<DataRow[]>([]);
    const [inputSearch, setInputSearch] = useState('');
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<string>('default');
    const selectedStoreRef = useRef(selectedStore);
    useEffect(() => {
        selectedStoreRef.current = selectedStore;
    }, [selectedStore]);
    const filteredRows = rows.filter((row) => {
        const searchs = inputSearch.split(/\s+/).filter((v) => v.length > 0);
        return [
            searchs.length == 0
                ? true
                : searchs
                      .map(
                          (s) =>
                              row.carId?.toUpperCase().includes(s.toUpperCase()) ||
                              moment(row.estimateTime).format('YYYY-MM-DD A hh:mm').includes(s.toUpperCase()),
                      )
                      .every((v) => v),
        ].every((v) => v);
    });
    const [mqttClient, setMqttClient] = useState<MqttClient | null>(null);
    const mqttClientRef = useRef(mqttClient);
    useEffect(() => {
        mqttClientRef.current = mqttClient;
        if (!mqttClient) {
            const mqttClient = mqtt.connect(process.env.REACT_APP_MQTT_BROKER_URL!);
            setMqttClient(mqttClient);
        } else {
            mqttClient.on('connect', () => {
                console.log('Connected to MQTT broker');
            });
            mqttClient.on('error', (err) => {
                console.error('MQTT Client Error:', err);
                mqttClient.end();
            });
            mqttClient.on('reconnect', () => {
                console.log('Reconnecting to MQTT broker');
            });
            mqttClient.on('message', (topic, message) => {
                console.log(`Message received on topic ${topic}:`, message.toString());
                refresh();
            });
            mqttClient.subscribe('database/table/car_queue/#', { qos: 0 }, (error) => {
                if (error) {
                    console.error('Subscribe to topics error:', error);
                    return;
                }
                console.log('Subscribed to topic: database/table/car_queue/#');
            });
        }
        return () => {
            if (mqttClient) {
                mqttClient.end();
                console.log('Disconnected to MQTT broker');
            }
        };
    }, [mqttClient]);
    const carQueuesRef = useRef<CarQueue[]>(carQueues);
    const reloadStores = async () => {
        const newStores = await api.store.get({}).then((ret) => ret.data || []);
        setStores(newStores);
        if (newStores.length == 1) setSelectedStore(newStores[0].storeId!);
    };
    const getCarQueues = async () => {
        return await api.carQueue
            .get({
                complete: 'N',
                cancel: 'N',
                estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime()),
                estimateTimeEd: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 24 * 60 * 60 * 1000),
                store: { storeId: selectedStoreRef.current },
                order: { estimateTime: 'asc' },
            })
            .then((ret) => ret.data || []);
    };
    const reloadCarQueues = async () => setCarQueues(await getCarQueues());
    const sideBar = useSideBar();
    const [prevSelectedIndex, setPrevSelectedIndex] = useState<number>(-1);
    const dashboardRef = useRef<HTMLDivElement | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        reloadStores();
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);
    useEffect(() => {
        reloadCarQueues();
    }, [selectedStore]);
    const enterFullscreen = () => {
        if (dashboardRef.current) {
            dashboardRef.current.requestFullscreen();
        }
    };
    const exitFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    };
    const refresh = async () => {
        const newCarQueues = await getCarQueues();
        const oldQueue = carQueuesRef.current
            .map((carQueue) => `${carQueue.carQueueId} ${carQueue.carId} ${carQueue.estimateTime} ${carQueue.complete} ${carQueue.cancel}`)
            .join(', ');
        const newQueue = newCarQueues
            .map((carQueue) => `${carQueue.carQueueId} ${carQueue.carId} ${carQueue.estimateTime} ${carQueue.complete} ${carQueue.cancel}`)
            .join(', ');
        if (oldQueue != newQueue) {
            setCarQueues(newCarQueues);
            page.simpleAlert.show({ autoHideDuration: 500, severity: 'warning', message: '有資料更新' });
        } else {
            // page.simpleAlert.show({ autoHideDuration: 2000, severity: 'info', message: '沒資料更新' });
        }
    };
    useEffect(() => {
        reloadCarQueues();
        const interval = setInterval(() => {
            if (mqttClientRef.current == null || !mqttClientRef.current.connected) refresh();
        }, 10 * 1000);
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        carQueuesRef.current = carQueues;
        let newRows: DataRow[] = carQueues.map((carQueue, idx) => ({
            ...carQueue,
            selected: false,
            index: idx,
            prevCarQueue: undefined,
            nextCarQueue: undefined,
            prevSpaces: 0,
            nextSpaces: 0,
            estimateUnixTime: new Date(carQueue.estimateTime!).getTime(),
            warnings: [],
        }));
        newRows = newRows.map((row, idx, arr) => {
            return {
                ...row,
                prevCarQueue: idx > 0 ? { ...arr[idx - 1] } : undefined,
                nextCarQueue: idx < arr.length - 1 ? arr[idx + 1] : undefined,
            };
        });
        newRows = newRows.map((row, idx, arr) => {
            if (row.prevCarQueue && row.estimateUnixTime - row.prevCarQueue.estimateUnixTime < washMicroSeconds)
                row.warnings.push(`距離前車時間小於${washMinutes}分鐘`);
            if (row.nextCarQueue && row.nextCarQueue.estimateUnixTime - row.estimateUnixTime < washMicroSeconds)
                row.warnings.push(`距離後車時間小於${washMinutes}分鐘`);
            const repeatTimes = arr.filter((v) => v.carId == row.carId).length;
            repeatTimes > 1 && row.warnings.push(`隊伍中車號 ${row.carId} 重複出現 ${repeatTimes} 次`);
            row.prevSpaces = row.prevCarQueue
                ? Math.min(9, Math.floor((row.estimateUnixTime - row.prevCarQueue.estimateUnixTime - washMicroSeconds) / washMicroSeconds))
                : 9;
            row.nextSpaces = row.nextCarQueue
                ? Math.min(9, Math.floor((row.nextCarQueue.estimateUnixTime - row.estimateUnixTime - washMicroSeconds) / washMicroSeconds))
                : 9;
            return { ...row };
        });
        setRows(newRows);
    }, [carQueues]);
    useEffect(() => {
        if (auth.isAuth) {
            api.user.get({ userId: auth.profile.userId, relations: { cars: true } }).then((ret) => {
                if (ret.data && ret.data.length > 0) setMyCarIds((ret.data[0].cars || []).map((car) => car.carId!));
            });
        } else {
            setMyCarIds([]);
        }
    }, [auth.isAuth]);

    return {
        page,
        classes,
        auth,
        screenSize,
        myCarIds,
        setMyCarIds,
        carQueues,
        setCarQueues,
        rows,
        setRows,
        inputSearch,
        setInputSearch,
        stores,
        setStores,
        selectedStore,
        setSelectedStore,
        filteredRows,
        reloadCarQueues,
        sideBar,
        prevSelectedIndex,
        setPrevSelectedIndex,
        dashboardRef,
        isFullscreen,
        setIsFullscreen,
        enterFullscreen,
        exitFullscreen,
    };
};

export const CarQueueDashboardPage = (): React.ReactElement => {
    const props = useCarQueueDashboardPage();
    const rowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderCollapse: 'collapse',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        height: 44,
    };
    return (
        <Page {...props.page} title='排序看板'>
            <Grid container flexDirection={'row'} height={'100%'} overflow={'hidden'} sx={{ userSelect: 'none' }}>
                <Grid
                    container
                    item
                    flex={1}
                    flexDirection={'column'}
                    padding={props.screenSize.mdUp ? 4 : 1}
                    height={'100%'}
                    overflow={'hidden'}
                    sx={{ backgroundColor: grey[100] }}
                >
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'center'} md={3} xs={6}>
                            <Select
                                value={props.selectedStore}
                                onChange={(e) => props.setSelectedStore(e.target.value)}
                                fullWidth
                                style={{ backgroundColor: 'white' }}
                            >
                                <MenuItem value={'default'} disabled>
                                    {'請選擇分店：'}
                                </MenuItem>
                                {props.stores.map((store) => (
                                    <MenuItem value={store.storeId}>{store.storeName}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>
                    <Grid
                        ref={props.dashboardRef}
                        container
                        item
                        flex={1}
                        padding={props.screenSize.mdUp ? 2 : 1}
                        borderRadius={2}
                        marginTop={2}
                        overflow={'hidden'}
                        sx={{ height: '100%', backgroundColor: 'white' }}
                    >
                        <Typography
                            component={'div'}
                            flex={1}
                            borderRadius={2}
                            sx={{
                                display: 'grid',
                                placeContent: 'start',
                                gridTemplateColumns: ['minmax(90px, 3fr)', 'minmax(180px, 6fr)'].join(' '),
                                gap: 0,
                                borderCollapse: 'collapse',
                                overflow: 'auto',
                                maxHeight: '100%',
                                backgroundColor: 'white',
                                border: '1px solid rgba(224, 224, 224, 1)',
                            }}
                        >
                            {['車牌號碼'].map((v) => (
                                <Typography key={v} component={'div'} sx={{ ...rowStyle }}>
                                    {v}
                                </Typography>
                            ))}
                            <Typography component={'div'} sx={{ ...rowStyle, position: 'relative' }}>
                                {'預估時間'}
                                {props.isFullscreen ? (
                                    <IconButton size={'small'} style={{ position: 'absolute', right: 0 }} onClick={props.exitFullscreen}>
                                        <FontAwesomeIcon icon={faCompress} fixedWidth />
                                    </IconButton>
                                ) : (
                                    <IconButton size={'small'} style={{ position: 'absolute', right: 0 }} onClick={props.enterFullscreen}>
                                        <FontAwesomeIcon icon={faExpand} fixedWidth />
                                    </IconButton>
                                )}
                            </Typography>
                            {props.filteredRows.map((row) => {
                                return (
                                    <Fragment key={row.carQueueId!}>
                                        <Typography
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            {(row.carId || '')
                                                .replace(/\b(\w)(\w)\b/g, '$1〇')
                                                .replace(/\b(\w)(\w)(\w)\b/g, '$1〇$3')
                                                .replace(/\b(\w)(\w)(\w)(\w)\b/g, '$1〇$3$4')}
                                        </Typography>
                                        <Typography
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            {row.estimateTime ? moment(row.estimateTime).format('YYYY-MM-DD A hh:mm') : ''}
                                        </Typography>
                                    </Fragment>
                                );
                            })}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        </Page>
    );
};
